// File Metadata:
// Filename: Scanner.js
// Version: 1.00
// Created: 2022-10-17 22:46:49
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import React, { Component } from 'react'
import Quagga from 'quagga'
import { Box, LinearProgress } from '@mui/material'
import './Scanner.css'

// Use a short beep sound in WAV format
const BEEP_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1NOTQrHxEGAQMKFCY4SVhqe4yZoaKcjnZZPCQLAvrz8vENHzNCWnGHmqGgnIxvVDYiB/jt5+Pq8wgYLUBYcIOQlI+GdV1BKxQA8+zo6fQFGCs8UmaBjpOPiHpeRzAXA/Xv7fH7DyQ3Sl91iJWan5qDb1c5JAr69fT3CRswQ1txh5qloZ2NcFU2Igj47ujk7PYKGi9CW3KGkpWSiHZbPScN/fPt7fL9EiY6UWB5jJiZlIZxVz0oD/339fX8BhYqPVZre4qRjIZyXEArFQH17Ovr9QYaLkFXb4SRlZCHd19DLhgD9/Dv8vwQJjlMYHaMl5qbmoVxWTokC/z39/kLHTNGXXSKnaamnZB0XDomCvn26uXu+A0cMUVbc4iXm5yXjHFYOiQK/Pf3+godM0ZddIqcp6aekHVcOiYK+PXp5O33DBsvRFpyhpeam5aMcVg6JAv89/f5Cx0zRl10ipynpp6QdVw6Jgr49enk7fcMGy9EWnKGl5qbloxxWDokC/z39/kLHTNGXXSKnKemnpB1XDomCvj16eTt9wwbL0RacoaXmpuWjHFYOiQL/Pf3+QsdM0ZddIqcp6aekHVcOiYK+PXp5O33DBsvRFpyhpeam5aMcVg6JAv89/f5Cx0zRl10ipynpp6QdVw6Jgr49enk7fcMGy9EWnKGl5qbloxxWDokC/z39/kLHTNGXXSKnKemnpB1XDomCvj16eTt9wwbL0RacoaXmpuWjHFYOiQL/Pf3+QsdM0ZddIqcp6aekHVcOiYK+PXp5O33DBsvRFpy'

class Scanner extends Component {
    constructor(props) {
        super(props)
        this.state = {
            nocamera: false,
            results: [],
            confidence: 0,
            scanStats: {
                total: 0,
                successful: 0,
                failed: 0
            },
            lastScanTime: null
        }
        // Initialize audio
        this.beep = new Audio(BEEP_SOUND)
        this.beep.addEventListener('error', (e) => {
            console.error('Audio error:', e)
        })
        this.scanning = false; // Add scanning state flag
        this.lastScannedCode = null;
        this.scanTimeout = null;
    }

    componentDidMount() {
        Quagga.init({
            inputStream: {
                name: 'Live',
                type: 'LiveStream',
                target: document.querySelector('#interactive'),
                constraints: {
                    facingMode: 'environment'
                }
            },
            numOfWorkers: navigator.hardwareConcurrency || 4,
            locate: true,
            decoder: {
                readers: ['code_128_reader'],
                debug: {
                    drawBoundingBox: true,
                    showPattern: true
                }
            }
        }, (err) => {
            if (err) {
                this.setState({ nocamera: true })
                console.error(err)
                return
            }
            Quagga.start()

            // Setup handlers
            Quagga.onDetected(this.onDetected)
            Quagga.onProcessed(this.onProcessed)
        })
    }

    componentWillUnmount() {
        Quagga.offDetected(this.onDetected)
        Quagga.offProcessed(this.onProcessed)
        Quagga.stop()
    }

    onProcessed = (result) => {
        const drawingCtx = Quagga.canvas.ctx.overlay
        const drawingCanvas = Quagga.canvas.dom.overlay

        if (result && drawingCtx && drawingCanvas) {
            // Clear canvas
            drawingCtx.clearRect(
                0, 0,
                parseInt(drawingCanvas.getAttribute('width')),
                parseInt(drawingCanvas.getAttribute('height'))
            )

            // Draw boxes if detected
            if (result.boxes) {
                result.boxes.filter(box => box !== result.box).forEach(box => {
                    Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, 
                        { color: 'yellow', lineWidth: 2 })
                })
            }

            // Draw result box
            if (result.box) {
                Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, 
                    { color: '#00F', lineWidth: 2 })
            }

            // Draw scan line
            if (result.codeResult && result.codeResult.code) {
                Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, 
                    { color: 'red', lineWidth: 3 })
            }
        }
    }

    onDetected = (result) => {
        // Prevent multiple scans while processing
        if (this.scanning) return;
        
        const code = result.codeResult.code;
        const confidence = result.codeResult.confidence;

        // Only process results with high confidence
        if (confidence < 0.8) return;

        // Prevent duplicate scans
        if (code === this.lastScannedCode) return;

        this.scanning = true;
        this.lastScannedCode = code;

        // Update progress bar
        this.setState({ confidence: 100 });

        // Play success sound
        try {
            this.beep.play().catch(console.warn);
        } catch (err) {
            console.warn('Beep error:', err);
        }

        // Stop scanning and return result
        Quagga.stop();
        this.props.onDetected(code);

        // Clear scanning flag after delay
        clearTimeout(this.scanTimeout);
        this.scanTimeout = setTimeout(() => {
            this.scanning = false;
            this.lastScannedCode = null;
        }, 2000); // 2 second cooldown
    }

    render() {
        return (
            <Box sx={{ 
                position: 'relative', 
                width: '100%',
                height: '100%',
                maxWidth: '100vw',
                aspectRatio: '16/9',
                margin: '0 auto',
                overflow: 'hidden'
            }}>
                <div id='interactive' className='viewport' />
                <div className="scan-line" />
                <Box sx={{ 
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1,
                    bgcolor: 'rgba(0,0,0,0.5)'
                }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={this.state.confidence || 0}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            '& .MuiLinearProgress-bar': {
                                bgcolor: this.state.confidence >= 100 ? 'success.main' : 'warning.main'
                            }
                        }}
                    />
                </Box>
                <div className="scanner-guides">
                    <div className="horizontal-guide" />
                    <div className="vertical-guide" />
                </div>
            </Box>
        )
    }
}

export default Scanner
